import { NotificationsPlugin } from "@pimo/notifications-plugin";
import { App } from "@pimo/pimo-app-builder";
import { DefaultOverlayNotificationPayload } from "@pimo/pimo-components";
import { generatePath } from "react-router-dom";

import {
  InOverlay,
  InOverlayEventNames,
  InOverlayEventPayload,
  InOverlayProps,
} from "../components/overlay/in-overlay.tsx";
import { UploadDialogFXRates } from "../components/upload-dialog/upload-dialog-fx-rates.tsx";
import { InAppState } from "./app.tsx";
import { APP_ROUTES } from "./constants.ts";
import { uploadFXRates } from "./helpers/fetch-helpers.ts";
import { fetchLogs } from "./helpers/fetch-logs.ts";

export const createOverlay = (app: App<InAppState>) => {
  const view = app.createOverlayView({ name: "overlay" });
  const overlay = view.addComponent<
    InOverlayProps,
    InOverlayEventNames,
    InOverlayEventPayload
  >({ component: InOverlay });

  overlay.on("overlay:show-logs", async () => {
    const logs = await fetchLogs();

    app.patchAppState({ logs });
  });
  overlay.on("overlay:settings-click", () => {
    app.navigate(APP_ROUTES.settingsNotifications);
    app.patchAppState({ isSidebarOpen: false });
  });
  overlay.on("overlay:upload-fx-rates", () => {
    app.patchAppState({ showUploadFXRatesPopup: true });
  });

  overlay.on("overlay:click-logo", () => app.navigate("/dashboard"));
  overlay.on("overlay:notification-read", (params) => {
    const notificationsPlugin = app.getPluginByName<
      NotificationsPlugin<InAppState>
    >("NotificationsPlugin");

    void notificationsPlugin?.handleOverlayNotificationReadEvent(
      params as { payload: DefaultOverlayNotificationPayload }
    );
  });
  overlay.mapState(
    ({
      isSidebarOpen,
      userProfile,
      operatingEntities,
      logs,
      banner,
      snackbar,
    }) => ({
      header: {
        logo: { big: "allianz.svg", small: "allianz-small.svg" },
        username: userProfile?.email ?? "",
        enableLogs: true,
        onLogsShow: () => {
          overlay.fireEvent("overlay:show-logs");
        },
      },
      isLegacy: true,
      notifications: {
        banner: banner,
      },
      currency:
        app.getAppState().operatingEntities?.length === 1
          ? app.getAppState().currency
          : "EUR",
      snackbar: snackbar,
      isSidebarOpen,
      logs: logs ?? [],
      menuEntries: [
        {
          title: "Dashboard",
          link: `${APP_ROUTES.dashboard}`,
          level: 0,
          active: window.location.pathname.includes(`${APP_ROUTES.dashboard}`),
          icon: "dashboard.svg",
        },

        {
          title: "OE Overview",
          link: `${APP_ROUTES.overview}`,
          level: 0,
          active: window.location.pathname.includes(`${APP_ROUTES.overview}`),
          icon: "clipboard.svg",
        },
        {
          title: "OE Initiatives",
          level: 0,
          items: operatingEntities?.map((oe) => {
            return {
              level: 1,
              link: generatePath(APP_ROUTES.operatingEntities, {
                oeId: String(oe.id ?? ""),
                viewname: "initiatives",
              }),
              title: oe.operatingEntity ?? "",
              active:
                window.location.pathname.includes(
                  `operating-entities/${oe.id ?? ""}/`
                ) ||
                window.location.pathname ===
                  `operating-entities/${oe.id ?? ""}`,
            };
          }),
          active: window.location.pathname.includes("operating-entities"),
          icon: "barchart.svg",
        },
      ].filter(Boolean) as InOverlayProps["menuEntries"],
      userProfile: userProfile,
    })
  );
  const uploadPopup = view.addComponent({
    component: UploadDialogFXRates,
    layoutProps: { xs: 12 },
  });

  uploadPopup.mapState(({ showUploadFXRatesPopup }) => {
    return {
      isOpen: showUploadFXRatesPopup ?? false,
      fileType: "excel",
      hideCommentField: true,
    };
  });

  uploadPopup.on("upload-dialog:close", () =>
    app.patchAppState({ showUploadFXRatesPopup: false })
  );

  uploadPopup.on("upload-dialog:upload-actual-rates", async ({ payload }) => {
    const file = payload?.file;

    if (!file) {
      return;
    }

    await uploadFXRates({ actualsFile: file });
  });
  uploadPopup.on("upload-dialog:upload-planned-rates", async ({ payload }) => {
    const file = payload?.file;

    if (!file) {
      return;
    }

    await uploadFXRates({ plannedRatesFile: file });
  });

  overlay.on("overlay:load-more-logs", async () => {
    const { logs } = app.getAppState();
    const offset = logs?.length || 0;
    const newLogs = await fetchLogs(offset, 50);
    app.patchAppState({ logs: [...(logs ?? []), ...newLogs] });
  });

  overlay.on("overlay:toggle-sidebar", () => {
    app.patchAppState({
      isSidebarOpen: !app.getAppState().isSidebarOpen,
    });
  });

  return overlay;
};
